import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import API from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
    RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis, AreaChart, Area
} from 'recharts';
import {
    Satellite, TrendingUp, MessageSquare, Hash, Search, Brain, RefreshCw,
    Globe, Radio, Loader2, Sparkles, Filter, Zap, Eye,
    ThumbsUp, ThumbsDown, Minus, ArrowUpRight, ArrowDownRight, ExternalLink,
    Users, BarChart3, Activity, Shield, Target, ChevronRight
} from 'lucide-react';
import EnginePredictionBanner from '../components/dashboard/EnginePredictionBanner';

const PLATFORM_ICONS = {
    twitter_x: { label: 'Twitter / X', color: '#1DA1F2', emoji: 'X' },
    instagram: { label: 'Instagram', color: '#E1306C', emoji: 'IG' },
    youtube: { label: 'YouTube', color: '#FF0000', emoji: 'YT' },
    reddit: { label: 'Reddit', color: '#FF4500', emoji: 'RD' },
    facebook: { label: 'Facebook', color: '#1877F2', emoji: 'FB' },
    google_trends: { label: 'Google Trends', color: '#4285F4', emoji: 'GT' },
    web_news: { label: 'Web / News', color: '#10B981', emoji: 'WEB' },
    telegram: { label: 'Telegram', color: '#0088CC', emoji: 'TG' },
};

export default function ExternalIntelligence() {
    const [searchParams] = useSearchParams();
    const urlMatch = searchParams.get('match') || '';
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [scraping, setScraping] = useState(false);
    const [selectedMatch, setSelectedMatch] = useState(urlMatch);
    const [upcomingMatches, setUpcomingMatches] = useState([]);
    const [activeSection, setActiveSection] = useState('overview');

    const fetchDashboard = useCallback(async (matchId) => {
        try {
            const url = matchId ? `/intelligence/advanced-dashboard?match_id=${matchId}` : '/intelligence/advanced-dashboard';
            const res = await API.get(url);
            setData(res.data);
        } catch { setData(null); }
        setLoading(false);
    }, []);

    useEffect(() => {
        fetchDashboard(selectedMatch);
        API.get('/fantasy/upcoming/matches').then(res => setUpcomingMatches(res.data?.matches || [])).catch(() => {});
    }, [selectedMatch, fetchDashboard]);

    const triggerScrape = async () => {
        setScraping(true);
        try {
            await API.post('/intelligence/scrape', {
                team_a: data?.match?.home?.short_name || '',
                team_b: data?.match?.away?.short_name || '',
                keywords: ['IPL 2026', '#IPL2026', data?.match?.home?.short_name, data?.match?.away?.short_name].filter(Boolean)
            });
            toast.success('Intelligence scrape triggered across all 8 platforms');
            setTimeout(() => fetchDashboard(selectedMatch), 5000);
        } catch { toast.error('Scrape failed'); }
        setScraping(false);
    };

    if (loading) {
        return (
            <div className="max-w-[1400px] mx-auto p-4 md:p-6">
                <div className="glass-card p-16 text-center border border-white/5">
                    <Loader2 className="w-10 h-10 text-cyan-400 animate-spin mx-auto" />
                    <div className="heading-primary text-lg mt-4 text-white">INITIALIZING INTELLIGENCE ENGINE</div>
                    <div className="text-xs text-white/20 mt-2 tech-mono">Aggregating 8 platforms...</div>
                </div>
            </div>
        );
    }

    const match = data?.match;
    const consensus = data?.overall_consensus || {};
    const platforms = data?.platforms || [];
    const heatmap = data?.sentiment_heatmap || [];
    const hashtags = data?.trending_hashtags || [];
    const experts = data?.expert_predictions || [];
    const topPosts = data?.top_posts || [];
    const gptAnalysis = data?.gpt_analysis;
    const trends = data?.google_trends || [];
    const scrapeStatus = data?.scrape_status || {};

    return (
        <div className="max-w-[1400px] mx-auto p-4 md:p-6 space-y-4" data-testid="external-intelligence-page">
            <EnginePredictionBanner engineKey="external_intelligence" matchId={selectedMatch || match?.id} />

            {/* ══════ HEADER BAR ══════ */}
            <div className="relative glass-card p-5 border border-white/5 overflow-hidden">
                <div className="absolute inset-0 opacity-[0.03]" style={{
                    backgroundImage: 'url(https://images.unsplash.com/photo-1750716413756-b66624b64ce4?w=1200&q=20)',
                    backgroundSize: 'cover', backgroundPosition: 'center', filter: 'grayscale(1)'
                }} />
                <div className="relative z-10 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-cyan-600 flex items-center justify-center">
                            <Satellite className="w-5 h-5 text-white" />
                        </div>
                        <div>
                            <h1 className="heading-primary text-base md:text-xl text-white tracking-tight">
                                EXTERNAL INTELLIGENCE ENGINE <span className="text-cyan-400">v2.0</span>
                            </h1>
                            <div className="flex items-center gap-3 text-[10px] text-white/30 tech-mono mt-0.5">
                                <span className="flex items-center gap-1">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> LIVE
                                </span>
                                <span>{scrapeStatus.platforms_scraped || 0} PLATFORMS</span>
                                <span>{scrapeStatus.total_items_in_db || 0} ITEMS</span>
                                {scrapeStatus.last_scrape && (
                                    <span>LAST: {new Date(scrapeStatus.last_scrape).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Select value={selectedMatch} onValueChange={v => setSelectedMatch(v === 'auto' ? '' : v)}>
                            <SelectTrigger className="w-44 h-8 bg-white/5 border-white/10 text-white text-xs" data-testid="match-selector">
                                <SelectValue placeholder="Auto-detect match" />
                            </SelectTrigger>
                            <SelectContent className="bg-zinc-950 border-white/10">
                                <SelectItem value="auto" className="text-white/60 text-xs">Auto-detect</SelectItem>
                                {upcomingMatches.slice(0, 12).map(m => (
                                    <SelectItem key={m.id} value={m.id} className="text-white/80 text-xs">
                                        {m.home?.short_name} vs {m.away?.short_name} {m.status === 'completed' ? '(Done)' : ''}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button onClick={triggerScrape} disabled={scraping} size="sm"
                            className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white text-xs h-8"
                            data-testid="scrape-all-btn">
                            {scraping ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5 mr-1" />}
                            Scrape All Platforms
                        </Button>
                    </div>
                </div>
            </div>

            {/* ══════ HERO: OVERALL CONSENSUS ══════ */}
            {match?.home && match?.away && (
                <div className="glass-card p-6 border border-white/5" data-testid="overall-consensus">
                    <div className="flex items-center gap-2 mb-4">
                        <Target className="w-4 h-4 text-amber-400" />
                        <span className="text-[10px] text-white/30 tech-mono tracking-widest">OVERALL SOCIAL MEDIA PREDICTION CONSENSUS</span>
                        <span className={`ml-auto text-[10px] tech-mono px-2 py-0.5 rounded ${
                            consensus.confidence === 'high' ? 'bg-green-500/10 text-green-400' :
                            consensus.confidence === 'medium' ? 'bg-amber-500/10 text-amber-400' :
                            'bg-white/5 text-white/30'
                        }`}>
                            {consensus.confidence?.toUpperCase()} CONFIDENCE | {consensus.total_items_analyzed || 0} items analyzed
                        </span>
                    </div>
                    <div className="flex items-center gap-6">
                        <div className="text-center min-w-[120px]">
                            <div className="heading-primary text-4xl md:text-5xl font-black" style={{ color: match.home.color }}>
                                {consensus.team_a_pct || 50}%
                            </div>
                            <div className="text-sm font-bold mt-1" style={{ color: match.home.color }}>{match.home.short_name}</div>
                            <div className="text-[10px] text-white/20">{match.home.name}</div>
                        </div>
                        <div className="flex-1">
                            <div className="h-8 bg-white/5 rounded-full overflow-hidden flex relative">
                                <div className="h-full rounded-l-full transition-all duration-1000 flex items-center justify-end pr-2"
                                    style={{ width: `${consensus.team_a_pct || 50}%`, backgroundColor: match.home.color }}>
                                    {(consensus.team_a_pct || 0) > 15 && (
                                        <span className="text-[10px] font-bold text-black/80">{consensus.team_a_pct}%</span>
                                    )}
                                </div>
                                <div className="h-full rounded-r-full flex-1 transition-all duration-1000 flex items-center pl-2"
                                    style={{ backgroundColor: match.away.color }}>
                                    {(consensus.team_b_pct || 0) > 15 && (
                                        <span className="text-[10px] font-bold text-black/80">{consensus.team_b_pct}%</span>
                                    )}
                                </div>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-[10px] font-bold text-white drop-shadow-lg bg-black/40 px-2 rounded">VS</span>
                                </div>
                            </div>
                            <div className="flex justify-between mt-2 text-[9px] text-white/20 tech-mono">
                                <span>SOCIAL SUPPORT: {match.home.short_name}</span>
                                <span>{consensus.total_items || 0} total intelligence items</span>
                                <span>SOCIAL SUPPORT: {match.away.short_name}</span>
                            </div>
                        </div>
                        <div className="text-center min-w-[120px]">
                            <div className="heading-primary text-4xl md:text-5xl font-black" style={{ color: match.away.color }}>
                                {consensus.team_b_pct || 50}%
                            </div>
                            <div className="text-sm font-bold mt-1" style={{ color: match.away.color }}>{match.away.short_name}</div>
                            <div className="text-[10px] text-white/20">{match.away.name}</div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════ PLATFORM CONSENSUS CARDS ══════ */}
            <div data-testid="platform-consensus-section">
                <div className="flex items-center gap-2 mb-3">
                    <BarChart3 className="w-4 h-4 text-cyan-400" />
                    <span className="text-[10px] text-white/30 tech-mono tracking-widest">PER-PLATFORM PREDICTION BREAKDOWN</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {platforms.map((p, i) => {
                        const cfg = PLATFORM_ICONS[p.source_key] || { label: p.name, color: '#666', emoji: '?' };
                        return (
                            <div key={i} className="glass-card p-4 border border-white/5 hover:border-white/10 transition-all group cursor-pointer"
                                data-testid={`platform-card-${p.source_key}`}>
                                <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-7 h-7 rounded-md flex items-center justify-center text-xs font-black"
                                            style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}>
                                            {cfg.emoji}
                                        </div>
                                        <div>
                                            <div className="text-[10px] font-bold text-white">{cfg.label}</div>
                                            <div className="text-[9px] text-white/20 tech-mono">{p.total_items} items</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-[9px] text-white/20 tech-mono">{p.engagement_total.toLocaleString()} eng.</div>
                                    </div>
                                </div>

                                {/* Team support bar */}
                                {(p.team_a_support > 0 || p.team_b_support > 0) ? (
                                    <>
                                        <div className="flex items-center justify-between text-[10px] mb-1">
                                            <span style={{ color: match?.home?.color }} className="font-bold">{match?.home?.short_name} {p.team_a_pct}%</span>
                                            <span style={{ color: match?.away?.color }} className="font-bold">{p.team_b_pct}% {match?.away?.short_name}</span>
                                        </div>
                                        <div className="h-2 bg-white/5 rounded-full overflow-hidden flex">
                                            <div className="h-full rounded-l-full transition-all" style={{ width: `${p.team_a_pct}%`, backgroundColor: match?.home?.color }} />
                                            <div className="h-full rounded-r-full flex-1" style={{ backgroundColor: match?.away?.color }} />
                                        </div>
                                    </>
                                ) : (
                                    <div className="h-2 bg-white/5 rounded-full" />
                                )}

                                {/* Sentiment mini */}
                                <div className="flex items-center gap-2 mt-2 text-[9px]">
                                    <span className="text-green-400">{p.sentiment?.positive || 0}</span>
                                    <span className="text-white/20">/</span>
                                    <span className="text-red-400">{p.sentiment?.negative || 0}</span>
                                    <span className="text-white/20">/</span>
                                    <span className="text-white/30">{p.sentiment?.neutral || 0}</span>
                                    <span className="text-white/10 ml-auto">{p.avg_sentiment_score > 0 ? '+' : ''}{p.avg_sentiment_score}</span>
                                </div>
                            </div>
                        );
                    })}
                    {platforms.length === 0 && (
                        <div className="col-span-4 glass-card p-8 text-center border border-white/5">
                            <Satellite className="w-8 h-8 text-white/10 mx-auto mb-2" />
                            <div className="text-sm text-white/20">No platform data yet. Click "Scrape All Platforms" to gather intelligence.</div>
                        </div>
                    )}
                </div>
            </div>

            {/* ══════ DEEP ANALYTICS ROW ══════ */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Sentiment Heatmap */}
                <div className="glass-card p-5 border border-white/5" data-testid="sentiment-heatmap">
                    <div className="flex items-center gap-2 mb-4">
                        <Activity className="w-4 h-4 text-purple-400" />
                        <h3 className="heading-primary text-sm text-white">SENTIMENT HEATMAP</h3>
                    </div>
                    {heatmap.length > 0 ? (
                        <div className="space-y-2">
                            <div className="grid grid-cols-[80px_1fr_1fr_1fr] gap-1 text-[9px] text-white/30 tech-mono mb-1">
                                <div>PLATFORM</div>
                                <div className="text-center text-green-400">POSITIVE</div>
                                <div className="text-center text-white/30">NEUTRAL</div>
                                <div className="text-center text-red-400">NEGATIVE</div>
                            </div>
                            {heatmap.map((h, i) => (
                                <div key={i} className="grid grid-cols-[80px_1fr_1fr_1fr] gap-1 items-center">
                                    <div className="text-[10px] text-white/50 font-medium">{h.platform_full}</div>
                                    <div className="h-6 rounded flex items-center justify-center text-[9px] font-bold text-black/80 transition-all"
                                        style={{ backgroundColor: `rgba(34,197,94,${Math.max(h.positive_pct / 100, 0.1)})` }}>
                                        {h.positive_pct}%
                                    </div>
                                    <div className="h-6 rounded flex items-center justify-center text-[9px] font-bold text-white/40 transition-all"
                                        style={{ backgroundColor: `rgba(255,255,255,${Math.max(h.neutral_pct / 300, 0.03)})` }}>
                                        {h.neutral_pct}%
                                    </div>
                                    <div className="h-6 rounded flex items-center justify-center text-[9px] font-bold text-black/80 transition-all"
                                        style={{ backgroundColor: `rgba(239,68,68,${Math.max(h.negative_pct / 100, 0.1)})` }}>
                                        {h.negative_pct}%
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-10 text-white/15 text-xs">No sentiment data — trigger a scrape</div>
                    )}
                </div>

                {/* Google Trends + Hashtags */}
                <div className="space-y-4">
                    {/* Google Trends */}
                    <div className="glass-card p-5 border border-white/5" data-testid="google-trends">
                        <div className="flex items-center gap-2 mb-3">
                            <TrendingUp className="w-4 h-4 text-amber-400" />
                            <h3 className="heading-primary text-sm text-white">GOOGLE TRENDS</h3>
                        </div>
                        {trends.length > 0 ? (
                            <div className="flex items-center gap-6">
                                {trends.map((t, i) => (
                                    <div key={i} className="flex-1 text-center">
                                        <div className="heading-primary text-2xl" style={{ color: t.color }}>{t.interest || 0}</div>
                                        <div className="text-xs font-bold mt-1" style={{ color: t.color }}>{t.team}</div>
                                        <div className="text-[9px] text-white/20">search interest</div>
                                    </div>
                                ))}
                                {trends.length === 0 && <div className="text-center text-xs text-white/15 py-4 w-full">No trends data</div>}
                            </div>
                        ) : (
                            <div className="text-center py-6 text-white/15 text-xs">No Google Trends data</div>
                        )}
                    </div>

                    {/* Trending Hashtags */}
                    <div className="glass-card p-5 border border-white/5" data-testid="trending-hashtags">
                        <div className="flex items-center gap-2 mb-3">
                            <Hash className="w-4 h-4 text-cyan-400" />
                            <h3 className="heading-primary text-sm text-white">TRENDING HASHTAGS</h3>
                            <span className="text-[10px] text-white/20 tech-mono ml-auto">{hashtags.length} unique</span>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {hashtags.slice(0, 20).map((h, i) => (
                                <span key={i} className="px-2 py-0.5 rounded text-[10px] border border-white/5 text-white/50 hover:text-cyan-400 hover:border-cyan-400/30 transition-colors cursor-pointer"
                                    style={{ opacity: Math.max(0.4, 1 - i * 0.04) }}>
                                    {h.tag} <span className="text-white/20">({h.count})</span>
                                </span>
                            ))}
                            {hashtags.length === 0 && <span className="text-xs text-white/15">No hashtags yet</span>}
                        </div>
                    </div>
                </div>
            </div>

            {/* ══════ EXPERT PREDICTIONS ══════ */}
            {experts.length > 0 && (
                <div className="glass-card p-5 border border-purple-500/10" data-testid="expert-predictions">
                    <div className="flex items-center gap-2 mb-4">
                        <Shield className="w-4 h-4 text-purple-400" />
                        <h3 className="heading-primary text-sm text-white">CRICKET EXPERT PREDICTIONS</h3>
                        <span className="text-[10px] text-white/20 tech-mono ml-auto">{experts.length} experts tracked</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {experts.slice(0, 8).map((e, i) => (
                            <div key={i} className={`p-3 rounded-lg border transition-all ${
                                e.favors === 'team_a' ? 'border-amber-500/10 bg-amber-500/[0.02]' :
                                e.favors === 'team_b' ? 'border-cyan-500/10 bg-cyan-500/[0.02]' :
                                'border-white/5 bg-white/[0.01]'
                            }`} data-testid={`expert-${i}`}>
                                <div className="flex items-start justify-between gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs text-white/70 line-clamp-2 leading-relaxed">{e.text}</div>
                                        <div className="flex items-center gap-2 mt-1.5">
                                            <span className="text-[10px] text-white/40 font-medium">@{e.author}</span>
                                            <span className="text-[9px] text-white/15 tech-mono uppercase">{PLATFORM_ICONS[e.source]?.label || e.source}</span>
                                            <span className="text-[9px] text-white/15">{e.engagement?.toLocaleString()} eng.</span>
                                        </div>
                                    </div>
                                    <div className={`text-[10px] font-bold px-2 py-0.5 rounded shrink-0 ${
                                        e.favors === 'team_a' ? 'text-amber-400 bg-amber-500/10' :
                                        e.favors === 'team_b' ? 'text-cyan-400 bg-cyan-500/10' :
                                        'text-white/30 bg-white/5'
                                    }`}>
                                        {e.favors === 'team_a' ? match?.home?.short_name :
                                         e.favors === 'team_b' ? match?.away?.short_name : 'NEUTRAL'}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ══════ GPT-5.2 ANALYSIS ══════ */}
            {gptAnalysis?.available && (
                <div className="glass-card p-5 border border-violet-500/10" data-testid="gpt-analysis">
                    <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-violet-400" />
                        <h3 className="heading-primary text-sm text-white">GPT-5.2 INTELLIGENCE SYNTHESIS</h3>
                    </div>
                    <div className="text-sm text-white/60 whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto custom-scrollbar">
                        {gptAnalysis.summary || gptAnalysis.raw_analysis || 'Processing...'}
                    </div>
                    {gptAnalysis.key_insights?.length > 0 && (
                        <div className="mt-3 space-y-1.5">
                            {gptAnalysis.key_insights.map((ins, i) => (
                                <div key={i} className="flex items-start gap-2 text-xs text-white/50">
                                    <Zap className="w-3 h-3 text-amber-400 mt-0.5 shrink-0" />
                                    <span>{ins}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {/* ══════ TOP SOCIAL MEDIA POSTS / LIVE FEED ══════ */}
            <div className="glass-card p-5 border border-white/5" data-testid="intelligence-feed">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Radio className="w-4 h-4 text-red-400 animate-pulse" />
                        <h3 className="heading-primary text-sm text-white">LIVE INTELLIGENCE FEED</h3>
                        <span className="text-[10px] text-white/15 tech-mono">{topPosts.length} posts</span>
                    </div>
                </div>
                <div className="space-y-2 max-h-[400px] overflow-y-auto custom-scrollbar">
                    {topPosts.slice(0, 20).map((post, i) => {
                        const cfg = PLATFORM_ICONS[post.source] || { label: '?', color: '#666', emoji: '?' };
                        const isPos = String(post.sentiment || '').includes('pos');
                        const isNeg = String(post.sentiment || '').includes('neg');
                        return (
                            <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] rounded-lg hover:bg-white/[0.04] transition-colors border border-transparent hover:border-white/5"
                                data-testid={`feed-item-${i}`}>
                                <div className="w-7 h-7 rounded-md flex items-center justify-center text-[9px] font-black shrink-0"
                                    style={{ backgroundColor: `${cfg.color}15`, color: cfg.color }}>
                                    {cfg.emoji}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-xs text-white/70 line-clamp-2 leading-relaxed">{post.text}</div>
                                    <div className="flex items-center gap-3 mt-1.5 text-[9px]">
                                        {post.author && <span className="text-white/30 font-medium">@{post.author}</span>}
                                        <span className="text-white/15 tech-mono">{cfg.label}</span>
                                        {post.likes > 0 && <span className="text-white/15"><ThumbsUp className="w-2.5 h-2.5 inline mr-0.5" />{post.likes}</span>}
                                        {post.comments > 0 && <span className="text-white/15"><MessageSquare className="w-2.5 h-2.5 inline mr-0.5" />{post.comments}</span>}
                                        {isPos && <span className="text-green-400/60">POSITIVE</span>}
                                        {isNeg && <span className="text-red-400/60">NEGATIVE</span>}
                                        {post.url && (
                                            <a href={post.url} target="_blank" rel="noreferrer" className="text-white/10 hover:text-cyan-400 transition-colors">
                                                <ExternalLink className="w-2.5 h-2.5" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    {topPosts.length === 0 && (
                        <div className="text-center py-10 text-white/15 text-xs">
                            No social media data yet. Click "Scrape All Platforms" to begin intelligence gathering.
                        </div>
                    )}
                </div>
            </div>

            {/* ══════ PLATFORM TOP POSTS (Expandable per platform) ══════ */}
            {platforms.filter(p => p.top_posts?.length > 0).length > 0 && (
                <div data-testid="platform-posts-section">
                    <div className="flex items-center gap-2 mb-3">
                        <Eye className="w-4 h-4 text-green-400" />
                        <span className="text-[10px] text-white/30 tech-mono tracking-widest">TOP POSTS PER PLATFORM</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {platforms.filter(p => p.top_posts?.length > 0).map((p, pi) => {
                            const cfg = PLATFORM_ICONS[p.source_key] || { label: p.name, color: '#666', emoji: '?' };
                            return (
                                <div key={pi} className="glass-card p-4 border border-white/5">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className="w-5 h-5 rounded flex items-center justify-center text-[8px] font-black"
                                            style={{ backgroundColor: `${cfg.color}20`, color: cfg.color }}>
                                            {cfg.emoji}
                                        </div>
                                        <span className="text-[10px] font-bold text-white/60">{cfg.label}</span>
                                        <span className="text-[9px] text-white/15 tech-mono ml-auto">{p.top_posts.length} top</span>
                                    </div>
                                    <div className="space-y-1.5">
                                        {p.top_posts.slice(0, 3).map((post, j) => (
                                            <div key={j} className="text-[10px] text-white/40 p-2 bg-white/[0.02] rounded border border-white/[0.03] line-clamp-2">
                                                {post.text}
                                                <div className="flex items-center gap-2 mt-1 text-[8px] text-white/15">
                                                    {post.author && <span>@{post.author}</span>}
                                                    {post.engagement > 0 && <span>{post.engagement.toLocaleString()} eng.</span>}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Platform hashtags */}
                                    {p.top_hashtags?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {p.top_hashtags.slice(0, 5).map((h, hi) => (
                                                <span key={hi} className="text-[8px] px-1.5 py-0.5 rounded bg-white/[0.03] border border-white/5" style={{ color: cfg.color }}>
                                                    {h.tag}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            <div className="text-center tech-mono text-[9px] text-white/10 py-2">
                V-One AI External Intelligence Engine v2.0 | 8 Platforms | Powered by Apify + GPT-5.2
            </div>
        </div>
    );
}
